import { TranslationKey } from '../translation/languages/translation';

export const MAINTENANCE_TASK = {
  STREAMING_MODE: 'streaming_mode',
  REBOOT: 'reboot',
  RESTART_SERVICES: 'restart_services',
  UPLOAD_APPLICATION_LOGS: 'upload_application_logs',
  NETWORK_TEST: 'network_test',
  SET_SIM_PRIORITY: 'set_sim_priority',
  SET_WIFI_PRIORITY: 'set_wifi_priority',
  FREE_DISK_SPACE: 'free_disk_space',
} as const;

export type MaintenanceTask = (typeof MAINTENANCE_TASK)[keyof typeof MAINTENANCE_TASK];

export const MAINTENANCE_TASK_VALUES = Object.values(MAINTENANCE_TASK) as [MaintenanceTask, ...MaintenanceTask[]];

export const MAINTENANCE_TASK_LABEL_KEY_MAP: Record<MaintenanceTask, TranslationKey> = {
  [MAINTENANCE_TASK.STREAMING_MODE]: 'maintenance.task.streaming_mode',
  [MAINTENANCE_TASK.REBOOT]: 'maintenance.task.reboot',
  [MAINTENANCE_TASK.RESTART_SERVICES]: 'maintenance.task.restart_services',
  [MAINTENANCE_TASK.UPLOAD_APPLICATION_LOGS]: 'maintenance.task.upload_application_logs',
  [MAINTENANCE_TASK.NETWORK_TEST]: 'maintenance.task.network_test',
  [MAINTENANCE_TASK.SET_SIM_PRIORITY]: 'maintenance.task.set_sim_priority',
  [MAINTENANCE_TASK.SET_WIFI_PRIORITY]: 'maintenance.task.set_wifi_priority',
  [MAINTENANCE_TASK.FREE_DISK_SPACE]: 'maintenance.task.free_disk_space',
};

const LEGACY_TASKS: MaintenanceTask[] = [MAINTENANCE_TASK.REBOOT, MAINTENANCE_TASK.RESTART_SERVICES];

const HAILO_V3_TASKS: MaintenanceTask[] = [
  ...LEGACY_TASKS,
  MAINTENANCE_TASK.STREAMING_MODE,
  MAINTENANCE_TASK.UPLOAD_APPLICATION_LOGS,
  MAINTENANCE_TASK.NETWORK_TEST,
];

const SEEED_V4_TASKS: MaintenanceTask[] = [
  ...HAILO_V3_TASKS,
  MAINTENANCE_TASK.SET_SIM_PRIORITY,
  MAINTENANCE_TASK.SET_WIFI_PRIORITY,
  MAINTENANCE_TASK.FREE_DISK_SPACE,
];

export const MAINTENANCE_TASKS_BY_GENERATION: Record<string, MaintenanceTask[]> = {
  'hailo-v3': HAILO_V3_TASKS,
  'seeed-v4': SEEED_V4_TASKS,
};

// Blank (old devices) or unrecognised gen falls back to legacy set which all devices support
export const getSupportedMaintenanceTasks = (generation: string | null = null): MaintenanceTask[] =>
  (generation && MAINTENANCE_TASKS_BY_GENERATION[generation]) || LEGACY_TASKS;

// Wifi sent by existing endpoint but still a queued task
const WIFI_DETAILS_UPDATE = 'wifi_details_update';

export type PendingMaintenanceTask = MaintenanceTask | typeof WIFI_DETAILS_UPDATE;

export const PENDING_MAINTENANCE_TASK_LABEL_KEY_MAP: Record<PendingMaintenanceTask, TranslationKey> = {
  ...MAINTENANCE_TASK_LABEL_KEY_MAP,
  [WIFI_DETAILS_UPDATE]: 'maintenance.task.wifi_details_update',
};

const PENDING_MAINTENANCE_TASKS = Object.keys(PENDING_MAINTENANCE_TASK_LABEL_KEY_MAP) as PendingMaintenanceTask[];

export const getPendingMaintenanceTasks = (
  maintenanceTasks: Partial<Record<PendingMaintenanceTask, boolean>> | null = null
): PendingMaintenanceTask[] => {
  if (!maintenanceTasks) return [];

  return PENDING_MAINTENANCE_TASKS.filter((task) => maintenanceTasks[task]);
};
